import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, History, ListChecks, Loader2, Pencil, Save, Send } from 'lucide-react';
import type { QuestionnaireStatus } from '@peles/shared';
import { StatusBadge } from '../components/common';
import { EntityModal } from '../components/common/EntityModal';
import {
  QuestionnaireAuditView,
  QuestionnaireEditorView,
  QuestionnaireReviewView,
  QuestionnaireTemplateSettings,
  SendQuestionnaireModal,
} from '../components/questionnaires';
import { useQuestionnaire, useSaveQuestionnaireAsTemplate } from '../hooks/useQuestionnaires';

const STATUS_COLORS: Record<QuestionnaireStatus, string> = {
  DRAFT: 'gray',
  SENT: 'blue',
  IN_PROGRESS: 'amber',
  SUBMITTED: 'green',
  REVIEWED: 'purple',
};

type Tab = 'questions' | 'answers' | 'audit';

/**
 * Producer questionnaire detail: build questions, review client answers
 * (with apply-to-event), and inspect the audit trail. Also used to edit
 * templates (event-only tabs hidden).
 */
export function QuestionnaireDetailPage() {
  const { questionnaireId } = useParams<{ eventId?: string; questionnaireId: string }>();
  const navigate = useNavigate();
  const { data: questionnaire, isLoading } = useQuestionnaire(questionnaireId);
  const [tab, setTab] = useState<Tab>('questions');
  const [showSend, setShowSend] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  if (isLoading || !questionnaire) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  const isTemplate = questionnaire.isTemplate;
  const backPath = isTemplate ? '/templates' : `/events/${questionnaire.eventId}/questionnaires`;

  const tabs: { key: Tab; label: string; icon: typeof Pencil }[] = isTemplate
    ? [{ key: 'questions', label: 'Questions', icon: Pencil }]
    : [
        { key: 'questions', label: 'Questions', icon: Pencil },
        { key: 'answers', label: 'Answers', icon: ListChecks },
        { key: 'audit', label: 'Audit', icon: History },
      ];

  return (
    <div>
      <button
        onClick={() => navigate(backPath)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft size={16} />
        {isTemplate ? 'Templates' : (questionnaire.eventName ?? 'Event')}
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">
              {isTemplate
                ? (questionnaire.templateName ?? questionnaire.title)
                : questionnaire.title}
            </h1>
            {isTemplate ? (
              <StatusBadge label="Template" color="amber" />
            ) : (
              <StatusBadge
                label={questionnaire.status.replace('_', ' ')}
                color={STATUS_COLORS[questionnaire.status]}
              />
            )}
          </div>
          {questionnaire.description && (
            <p className="mt-1 text-sm text-gray-500 max-w-2xl">{questionnaire.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isTemplate && (
            <>
              <button
                onClick={() => setShowSaveTemplate(true)}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                data-testid="save-as-template-button"
              >
                <Save size={16} />
                Save as Template
              </button>
              <button
                onClick={() => setShowSend(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                data-testid="send-questionnaire-button"
              >
                <Send size={16} />
                Send to Client
              </button>
            </>
          )}
        </div>
      </div>

      {tabs.length > 1 && (
        <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                tab === key
                  ? 'border-primary-600 text-primary-700 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${key}`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      )}

      {isTemplate && <QuestionnaireTemplateSettings questionnaire={questionnaire} />}

      {tab === 'questions' && <QuestionnaireEditorView questionnaire={questionnaire} />}
      {tab === 'answers' && <QuestionnaireReviewView questionnaire={questionnaire} />}
      {tab === 'audit' && <QuestionnaireAuditView questionnaireId={questionnaire.id} />}

      <SendQuestionnaireModal
        open={showSend}
        onClose={() => setShowSend(false)}
        questionnaireId={questionnaire.id}
        sentTo={questionnaire.sentTo}
      />

      <SaveAsTemplateModal
        open={showSaveTemplate}
        onClose={() => setShowSaveTemplate(false)}
        questionnaireId={questionnaire.id}
        defaultName={questionnaire.title}
      />
    </div>
  );
}

function SaveAsTemplateModal({
  open,
  onClose,
  questionnaireId,
  defaultName,
}: {
  open: boolean;
  onClose: () => void;
  questionnaireId: string;
  defaultName: string;
}) {
  const [templateName, setTemplateName] = useState(defaultName);
  const saveAsTemplate = useSaveQuestionnaireAsTemplate();

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Save as Template"
      size="sm"
      footer={
        <button
          onClick={() =>
            saveAsTemplate.mutate(
              { id: questionnaireId, templateName: templateName.trim() },
              { onSuccess: onClose },
            )
          }
          disabled={!templateName.trim() || saveAsTemplate.isPending}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
          data-testid="save-template-submit"
        >
          {saveAsTemplate.isPending ? 'Saving…' : 'Save Template'}
        </button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Answers and event-specific links are stripped — the template keeps the questions and their
          data links.
        </p>
        <div>
          <label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-1">
            Template name
          </label>
          <input
            id="template-name"
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
    </EntityModal>
  );
}
